import { motion } from "framer-motion";

interface BrandMarkProps {
  size?: number;
  animated?: boolean;
  className?: string;
}

/**
 * Trendi Smart — abstract symbol mark.
 * Concept: a neural "spark" — three orbiting nodes connected to a central
 * pulse, forming an upward trend. No letters. Pure geometry + gradient.
 */
export default function BrandMark({ size = 36, animated = true, className = "" }: BrandMarkProps) {
  const Wrapper: any = animated ? motion.div : "div";
  return (
    <Wrapper
      className={`relative inline-block ${className}`}
      style={{ width: size, height: size }}
      {...(animated
        ? {
            whileHover: { scale: 1.08, rotate: 8 },
            transition: { type: "spring", stiffness: 280, damping: 16 },
          }
        : {})}
    >
      <svg viewBox="0 0 64 64" width={size} height={size} fill="none" aria-label="Trendi Smart">
        <defs>
          <linearGradient id="bm-core" x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="rgb(45 212 191)" />
            <stop offset="60%" stopColor="rgb(0 150 136)" />
            <stop offset="100%" stopColor="rgb(13 75 92)" />
          </linearGradient>
          <linearGradient id="bm-arc" x1="0" y1="64" x2="64" y2="0" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="rgb(0 150 136)" />
            <stop offset="100%" stopColor="rgb(255 152 0)" />
          </linearGradient>
          <radialGradient id="bm-pulse" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
            <stop offset="60%" stopColor="rgba(255,255,255,0.15)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
          <filter id="bm-soft" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="0.8" />
          </filter>
        </defs>

        {/* Outer orbit ring (3/4 arc) */}
        <path
          d="M 12 32 A 20 20 0 1 1 32 52"
          stroke="url(#bm-arc)"
          strokeWidth="3.5"
          strokeLinecap="round"
          fill="none"
        />

        {/* Inner connecting lines forming an upward triangle */}
        <path
          d="M 18 44 L 32 22 L 48 38"
          stroke="url(#bm-core)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />

        {/* Pulse halo */}
        <circle cx="32" cy="32" r="14" fill="url(#bm-pulse)" filter="url(#bm-soft)" />

        {/* Central node */}
        <circle cx="32" cy="22" r="5" fill="url(#bm-core)" />
        <circle cx="32" cy="22" r="2" fill="white" opacity="0.95" />

        {/* Orbiting nodes */}
        <circle cx="18" cy="44" r="3.5" fill="rgb(0 150 136)" />
        <circle cx="48" cy="38" r="4.5" fill="rgb(255 152 0)" />
        <circle cx="48" cy="38" r="1.6" fill="white" />
      </svg>
    </Wrapper>
  );
}
